import { useEffect, useState } from 'react';
import { Group, Menu, Button, Text, ActionIcon, Tooltip, Divider, Avatar, Badge } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { NavLink, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import TopRightMenu from './TopRightMenu';
import { useAuth } from '../store/auth';
import { api, imageUrl } from '../api/client';
import { initialsOf } from '../lib/initials';
import { APP_VERSION } from '../version';
import {
  IconClipboard, IconCaliper, IconEndMill, IconSpool,
  IconCustomer, IconStats, IconAdmin, IconEject,
} from './icons/CncIcons';

const navIconStyle = { display: 'inline-flex', alignItems: 'center', gap: 6 } as const;

const linkStyle = ({ isActive }: { isActive: boolean }) => ({
  textDecoration: 'none',
  color: isActive ? 'var(--mantine-color-orange-6)' : 'inherit',
  padding: '8px 12px',
  borderRadius: 4,
  fontWeight: isActive ? 600 : 400,
});

export default function Nav() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);

  // Admins: poll the pending access-request count for the Admin badge.
  useEffect(() => {
    if (me?.role !== 'admin') { setPendingCount(0); return; }
    let stop = false;
    const load = () => api.get<{ count: number }>('/admin/pending-count')
      .then((r) => { if (!stop) setPendingCount(r.count); })
      .catch(() => { /* non-fatal */ });
    load();
    const t = setInterval(load, 60000);
    return () => { stop = true; clearInterval(t); };
  }, [me?.role]);

  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap">
      <Group gap="md" wrap="nowrap">
        <Group gap={8} style={{ cursor: 'pointer' }} onClick={() => navigate('/orders')}>
          <Logo size={32} />
          <Text fw={700} size="lg" style={{ letterSpacing: '0.1em' }}>CYCLOTRON</Text>
        </Group>
        <Group gap={4} ml="xl" wrap="nowrap">
          <NavLink to="/orders" style={linkStyle} data-tour="nav-orders">
            <span style={navIconStyle}><IconClipboard size={16} />Orders</span>
          </NavLink>
          <Menu trigger="hover" openDelay={0} closeDelay={120}>
            <Menu.Target>
              <Button variant="subtle" color="gray" rightSection={<IconChevronDown size={14} />} leftSection={<IconEndMill size={16} />} data-tour="nav-lab">
                Lab
              </Button>
            </Menu.Target>
            <Menu.Dropdown data-tour="nav-lab-menu">
              <Menu.Item leftSection={<IconCaliper size={16} />} onClick={() => navigate('/lab/technicians')} data-tour="nav-lab-technicians">Lab Technicians</Menu.Item>
              <Menu.Item leftSection={<IconEndMill size={16} />} onClick={() => navigate('/lab/equipment')} data-tour="nav-lab-equipment">Lab Equipment</Menu.Item>
              <Menu.Item leftSection={<IconCaliper size={16} />} onClick={() => navigate('/lab/accessories')} data-tour="nav-lab-accessories">Lab Accessories</Menu.Item>
              <Menu.Item leftSection={<IconSpool size={16} />} onClick={() => navigate('/lab/consumables')} data-tour="nav-lab-consumables">Lab Consumables</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          <NavLink to="/customers" style={linkStyle} data-tour="nav-customers">
            <span style={navIconStyle}><IconCustomer size={16} />Customers</span>
          </NavLink>
          <NavLink to="/statistics" style={linkStyle} data-tour="nav-statistics">
            <span style={navIconStyle}><IconStats size={16} />Statistics</span>
          </NavLink>
          {me?.role === 'admin' && (
            <NavLink to="/admin" style={linkStyle} data-tour="nav-admin">
              <span style={navIconStyle}>
                <IconAdmin size={16} />Admin
                {pendingCount > 0 && (
                  <Badge size="sm" circle color="red" variant="filled" aria-label={`${pendingCount} pending access requests`}>{pendingCount}</Badge>
                )}
              </span>
            </NavLink>
          )}
        </Group>
      </Group>
      <Group gap="md" wrap="nowrap">
        <Tooltip label="CYCLOTRON version" position="bottom" withArrow openDelay={200}>
          <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }} data-tour="nav-version">v{APP_VERSION}</Text>
        </Tooltip>
        <Divider orientation="vertical" />
        <TopRightMenu />
        <Divider orientation="vertical" />
        <Group gap={8} wrap="nowrap" data-tour="nav-user">
          <Avatar
            src={imageUrl(me?.imagePath, true)}
            radius="xl"
            size={28}
            color="orange"
          >
            {initialsOf(me?.displayName, me?.username ?? '')}
          </Avatar>
          <Text size="sm" c="dimmed">
            {me?.displayName ?? me?.username} <Text span size="xs" c="gray">({me?.role})</Text>
          </Text>
        </Group>
        <Tooltip label="Log out" position="bottom" withArrow openDelay={200}>
          <ActionIcon variant="subtle" color="gray" onClick={async () => { await logout(); navigate('/login'); }} aria-label="Log out" data-tour="nav-logout">
            <IconEject size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}
